import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday } from 'date-fns';
import { RotateCcw } from 'lucide-react';
import './CalendarView.css';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: 'todo' | 'done';
  dueDate?: string;
  recurrence?: string | null;
  nextDueDate?: string | null;
  founderId?: string;
  Founder?: {
    name: string;
  };
}

interface CalendarViewProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onCreateTask: (date: Date) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ tasks, onTaskClick, onCreateTask }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Debug: Log tasks when they change
  useEffect(() => {
    console.log('CalendarView tasks updated:', tasks);
    console.log('Total tasks received:', tasks.length);
    tasks.forEach((task, index) => {
      console.log(`Task ${index + 1}:`, {
        title: task.title,
        dueDate: task.dueDate,
        recurrence: task.recurrence,
        nextDueDate: task.nextDueDate,
        status: task.status
      });
      if (task.dueDate) {
        console.log(`Task "${task.title}" due date: ${task.dueDate} (parsed: ${new Date(task.dueDate)})`);
      }
      if (task.recurrence) {
        console.log(`Task "${task.title}" is recurring: ${task.recurrence}, nextDueDate: ${task.nextDueDate}`);
      }
    });
  }, [tasks]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done':
        return '#10b981'; // green
      case 'todo':
      default:
        return '#ef4444'; // red
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'done':
        return 'Done';
      case 'todo':
      default:
        return 'To Do';
    }
  };

  const getTasksForDate = useCallback((date: Date) => {
    return tasks.filter(task => {
      if (!task.dueDate) return false;
      try {
        // Parse the task date as a local date to avoid timezone issues
        // If task.dueDate is in YYYY-MM-DD format, create a local date
        const taskDateStr = task.dueDate;
        const [year, month, day] = taskDateStr.split('-').map(Number);
        const taskDate = new Date(year, month - 1, day); // month is 0-indexed
        
        // For recurring tasks, check if the date matches the recurrence pattern
        if (task.recurrence) {
          const isOriginalDate = isSameDay(taskDate, date);
          if (isOriginalDate) {
            console.log(`Recurring task "${task.title}" matches original date:`, format(date, 'yyyy-MM-dd'));
            return true;
          }
          
          // Check if this date matches the recurrence pattern
          const daysDiff = Math.floor((date.getTime() - taskDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (task.recurrence === 'weekly' && daysDiff >= 0 && daysDiff % 7 === 0) {
            console.log(`Recurring task "${task.title}" matches weekly pattern for date:`, format(date, 'yyyy-MM-dd'), `(daysDiff: ${daysDiff})`);
            return true;
          } else if (task.recurrence === 'monthly' && daysDiff >= 0 && daysDiff % 30 === 0) {
            console.log(`Recurring task "${task.title}" matches monthly pattern for date:`, format(date, 'yyyy-MM-dd'), `(daysDiff: ${daysDiff})`);
            return true;
          } else if (task.recurrence === 'quarterly' && daysDiff >= 0 && daysDiff % 90 === 0) {
            console.log(`Recurring task "${task.title}" matches quarterly pattern for date:`, format(date, 'yyyy-MM-dd'), `(daysDiff: ${daysDiff})`);
            return true;
          } else if (task.recurrence === 'daily' && daysDiff >= 0) {
            console.log(`Recurring task "${task.title}" matches daily pattern for date:`, format(date, 'yyyy-MM-dd'), `(daysDiff: ${daysDiff})`);
            return true;
          }
          
          return false;
        }
        
        // For non-recurring tasks, check exact date match
        const isMatch = isSameDay(taskDate, date);
        if (isMatch) {
          console.log('Task matches date:', {
            taskTitle: task.title,
            taskDueDate: task.dueDate,
            taskDate: taskDate,
            selectedDate: date,
            formattedTaskDate: format(taskDate, 'yyyy-MM-dd'),
            formattedSelectedDate: format(date, 'yyyy-MM-dd')
          });
        }
        return isMatch;
      } catch (error) {
        console.warn('Invalid date format:', task.dueDate);
        return false;
      }
    });
  }, [tasks]);

  const renderCalendarDays = () => {
    try {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(monthStart);
      const startDate = startOfWeek(monthStart);
      const endDate = endOfWeek(monthEnd);

      console.log('Calendar rendering debug:', {
        currentDate: format(currentDate, 'yyyy-MM-dd'),
        monthStart: format(monthStart, 'yyyy-MM-dd'),
        monthEnd: format(monthEnd, 'yyyy-MM-dd'),
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      });

      const rows = [];
      let day = startDate;

      // Generate calendar weeks from startDate to endDate
      while (day <= endDate) {
        const days = [];
        for (let dayOfWeek = 0; dayOfWeek < 7 && day <= endDate; dayOfWeek++) {
          const dayTasks = getTasksForDate(day);
          const isCurrentMonth = isSameMonth(day, monthStart);
          const isCurrentDay = isToday(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);

          days.push(
            <div
              key={day.toString()}
              className={`calendar-day ${isCurrentMonth ? 'current-month' : 'other-month'} ${isCurrentDay ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                console.log('Calendar day clicked:', day, 'Formatted:', format(day, 'yyyy-MM-dd'));
                setSelectedDate(day);
                setSelectedTask(null); // Close task panel when clicking on a day
              }}
            >
              <div className="day-number">{format(day, 'd')}</div>
              <div className="day-tasks">
                {dayTasks.slice(0, 2).map((task, index) => (
                  <div
                    key={task.id}
                    className="task-item-mini"
                    style={{ 
                      borderLeftColor: getStatusColor(task.status),
                      position: 'relative'
                    }}
                    title={`${getStatusText(task.status)}: ${task.title}${task.recurrence ? ` (${task.recurrence})` : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTask(task);
                      onTaskClick(task);
                    }}
                  >
                    {task.title.length > 20 ? `${task.title.substring(0, 20)}...` : task.title}
                    {task.recurrence && (
                      <div
                        className="calendar-recurring-icon"
                        title={`Recurring: ${task.recurrence}`}
                      >
                        <RotateCcw size={8} />
                      </div>
                    )}
                  </div>
                ))}
                {dayTasks.length > 2 && (
                  <div className="more-tasks">+{dayTasks.length - 2}</div>
                )}
              </div>
            </div>
          );
          day = addDays(day, 1);
        }
        rows.push(
          <div key={rows.length} className="calendar-week">
            {days}
          </div>
        );
      }
      return rows;
    } catch (error) {
      console.error('Error rendering calendar days:', error);
      return <div>Error loading calendar</div>;
    }
  };


  const handleCalendarClick = (e: React.MouseEvent) => {
    // Only close if clicking on the calendar container itself, not on child elements
    if (e.target === e.currentTarget) {
      setSelectedTask(null);
    }
  };

  return (
    <div className="calendar-view" onClick={handleCalendarClick}>
      {/* Task Details Panel */}
      {selectedTask && (
        <div className="task-details-panel" onClick={(e) => e.stopPropagation()}>
          <div className="task-details-header">
            <h3 className="task-title-header">{selectedTask.title}</h3>
            <button 
              className="close-task-btn"
              onClick={() => setSelectedTask(null)}
            >
              ×
            </button>
          </div>
          <div className="task-details-content">
            <div className="task-details-column">
              {selectedTask.description && (
                <div className="task-detail-item">
                  <strong>Description:</strong> 
                  <div className="task-description-text">{selectedTask.description}</div>
                </div>
              )}
              <div className="task-detail-item">
                <strong>Status:</strong> 
                <span 
                  className="task-status-badge"
                  style={{ backgroundColor: getStatusColor(selectedTask.status) }}
                >
                  {getStatusText(selectedTask.status)}
                </span>
              </div>
            </div>
            <div className="task-details-column">
              {selectedTask.dueDate && (
                <div className="task-detail-item">
                  <strong>Due Date:</strong> 
                  <div className="task-description-text">{
                    (() => {
                      // Parse as local date to avoid timezone issues
                      const [year, month, day] = selectedTask.dueDate.split('-').map(Number);
                      const localDate = new Date(year, month - 1, day);
                      return localDate.toLocaleDateString();
                    })()
                  }</div>
                </div>
              )}
              {selectedTask.recurrence && (
                <div className="task-detail-item">
                  <strong>Recurrence:</strong> 
                  <div className="task-description-text" style={{ 
                    backgroundColor: '#e0f2fe', 
                    padding: '4px 8px', 
                    borderRadius: '4px',
                    display: 'inline-block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#0369a1'
                  }}>
                    {selectedTask.recurrence}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calendar-header" onClick={(e) => e.stopPropagation()}>
        <div className="calendar-nav">
          <button onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
            ‹
          </button>
          <h2>{format(currentDate, 'MMMM yyyy')}</h2>
          <button onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
            ›
          </button>
        </div>
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-dot" style={{ backgroundColor: '#ef4444' }}></div>
            <span>To Do</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot" style={{ backgroundColor: '#f59e0b' }}></div>
            <span>In Progress</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot" style={{ backgroundColor: '#10b981' }}></div>
            <span>Done</span>
          </div>
        </div>
      </div>

      <div className="calendar-grid" onClick={(e) => e.stopPropagation()}>
        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        {renderCalendarDays()}
      </div>

    </div>
  );
};

export default CalendarView;
